/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

import { RunSchema } from './chat.js';

/**
 * The gates a request passes on its way to the user, in order. Each time one turns the work back, the run's metrics
 * count it under `fail_<gate>`; the distribution says where the generator loses most.
 */
export const GATES = [
  'review',
  'fixed_layer',
  'frontend_types',
  'modules',
  'go_vet',
  'go_test',
  'app_start',
  'layout',
  'build',
  'deploy',
  'device_check',
  'runtime',
  'time',
  'assistant',
] as const;
export type Gate = (typeof GATES)[number];

export const GATE_LABELS: Record<Gate, string> = {
  review: 'Code review',
  fixed_layer: 'Fixed layer',
  frontend_types: 'Frontend types',
  modules: 'Dependencies',
  go_vet: 'go vet',
  go_test: 'Go tests',
  app_start: 'App start',
  layout: 'Layout',
  build: 'Build',
  deploy: 'Delivery',
  device_check: 'Device check',
  runtime: 'On the device',
  time: 'Time limit',
  assistant: 'Assistant',
};

/** The metrics key under which failures at a gate are counted. */
export const gateMetric = (gate: string): string => `fail_${gate}`;

/** The gate a `fail_...` metrics key counts, or undefined for any other key. */
export const gateOfMetric = (key: string): string | undefined =>
  key.startsWith('fail_') ? key.slice('fail_'.length) : undefined;

/** What the dashboard sees of a run: its outcome and numbers, not the words of the request or the plan. */
export const RunSummarySchema = RunSchema.pick({
  project_id: true,
  run_id: true,
  status: true,
  source: true,
  version: true,
  metrics: true,
  created_at: true,
  updated_at: true,
});
export type RunSummary = z.infer<typeof RunSummarySchema>;

export const RUN_METRICS_MAX_DAYS = 92;

export const RunMetricsQuerySchema = z.object({
  /** How many days back to look, today included. */
  days: z.coerce.number().int().min(1).max(RUN_METRICS_MAX_DAYS).default(30),
});

export const ListRunSummariesResponseSchema = z.object({
  runs: z.array(RunSummarySchema),
  /** The start of the period the runs were taken from. */
  since: z.string().datetime(),
});
export type ListRunSummariesResponse = z.infer<typeof ListRunSummariesResponseSchema>;
